// ImageList.tsx
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import styles from "./ImageList.module.css";

// --- API Imports ---
import { fetchVocabularyWords } from "../API/Vocabulary/VocabularyAPI";
// fetchCourseOrderData now handled by LanguageControlsGrid
import { VocabularyData } from "../API/types";
import { callOpenAI, generateImage, dataLoaderUri, mobileAppUri } from "../API/API";
import apiClient from "../services/apiClient";

// --- Shared Components ---
import LanguageControlsGrid, { AVAILABLE_LANGUAGES, PROFICIENCY_LEVELS, DIFFICULTIES, DOMAINS } from "../shared/LanguageControlsGrid/LanguageControlsGrid";

// --- GenAI Payload Imports ---
import {
  buildImageDescriptionPrompt,
  buildGenAiInitialImagePromptPayload,
} from "../GenAIPrompts/GenAIPrompts";

// --- Language Constants ---
const LANG_CODE_MAP: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};

// --- Helper Functions ---
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    var r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function createNaturalSceneFromWords(words: string[]): string {
  const wordSet = new Set(words.map(w => w.toLowerCase()));
  
  // Family-related scenes
  const familyWords = ['madre', 'padre', 'hijo', 'hija', 'hermano', 'hermana', 'niño', 'niña', 'hombre', 'mujer', 'persona', 'family', 'mother', 'father', 'son', 'daughter', 'brother', 'sister', 'boy', 'girl', 'man', 'woman', 'person'];
  if (familyWords.some(word => wordSet.has(word))) {
    return "a happy family gathering in their home, with parents, children, and siblings interacting naturally together";
  }
  
  // Food-related scenes
  const foodWords = ['comida', 'comer', 'beber', 'agua', 'pan', 'fruta', 'food', 'eat', 'drink', 'water', 'bread', 'fruit'];
  if (foodWords.some(word => wordSet.has(word))) {
    return "people enjoying a meal together at a dining table with various foods and drinks";
  }
  
  // House/home scenes
  const houseWords = ['casa', 'cocina', 'sala', 'cuarto', 'baño', 'house', 'kitchen', 'living room', 'room', 'bathroom'];
  if (houseWords.some(word => wordSet.has(word))) {
    return "a cozy home interior showing different rooms where people live and spend time";
  }
  
  // School/education scenes
  const schoolWords = ['escuela', 'estudiar', 'libro', 'escribir', 'leer', 'school', 'study', 'book', 'write', 'read'];
  if (schoolWords.some(word => wordSet.has(word))) {
    return "students and teachers in a classroom setting, learning and studying together";
  }
  
  // Work/profession scenes
  const workWords = ['trabajo', 'trabajar', 'doctor', 'maestro', 'work', 'doctor', 'teacher'];
  if (workWords.some(word => wordSet.has(word))) {
    return "people in their workplace environments, showing different professions and activities";
  }
  
  // Transportation scenes
  const transportWords = ['carro', 'bus', 'tren', 'bicicleta', 'car', 'bus', 'train', 'bicycle'];
  if (transportWords.some(word => wordSet.has(word))) {
    return "people using various forms of transportation in a busy street scene";
  }
  
  // Animals scenes
  const animalWords = ['perro', 'gato', 'pájaro', 'pez', 'dog', 'cat', 'bird', 'fish'];
  if (animalWords.some(word => wordSet.has(word))) {
    return "people interacting with various animals in a park or home setting";
  }
  
  // Colors/objects scenes
  const colorWords = ['rojo', 'azul', 'verde', 'amarillo', 'red', 'blue', 'green', 'yellow'];
  if (colorWords.some(word => wordSet.has(word))) {
    return "a vibrant scene with people surrounded by colorful objects and environments";
  }
  
  // Default scene for any other vocabulary
  return "people in a everyday life scene that naturally incorporates the vocabulary concepts being learned";
}

// --- Interface Definitions ---
interface ImageListData {
  id: string;
  selectedWords: string[];
  imageUrl?: string;
  imageDescription?: string;
  isGenerating: boolean;
  error?: string;
  sectionId: string;
  targetLanguage: string;
  baseLanguage: string;
  domain: string;
  proficiencyLevel: string;
  difficulty: string;
  wordMappings: { [key: string]: string }; // target word -> base word mapping
  multiLanguageWordList: {
    [languageCode: string]: Array<{targetWord: string, baseWord: string}>; // e.g., "EN": [{"targetWord": "house", "baseWord": "house"}], "DE": [{"targetWord": "Haus", "baseWord": "house"}]
  };
}

interface VocabularyWord {
  word: string;
  baseTranslation: string;
}

interface VocabularyGroupData {
  targetWord: string;
  allVariants: VocabularyData[];
}

const ImageList: React.FC = () => {
  // --- State Variables ---
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [section, setSection] = useState<string>("");
  const [domain, setDomain] = useState<string>(DOMAINS[0]);
  const [proficiencyLevel, setProficiencyLevel] = useState<string>(PROFICIENCY_LEVELS[2]);
  const [difficulty, setDifficulty] = useState<string>(DIFFICULTIES[1]);
  
  // courseSections now handled by LanguageControlsGrid
  const [vocabularyWords, setVocabularyWords] = useState<string[]>([]);
  const [vocabularyGroupData, setVocabularyGroupData] = useState<VocabularyGroupData[]>([]);
  const [selectedWordsBucket, setSelectedWordsBucket] = useState<Set<string>>(new Set());
  
  const [imageListData, setImageListData] = useState<ImageListData[]>([]);
  const [existingImages, setExistingImages] = useState<ImageListData[]>([]);
  const [filteredExistingImages, setFilteredExistingImages] = useState<ImageListData[]>([]);
  const [baseLanguageFilter, setBaseLanguageFilter] = useState<string>("all");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // loadingSections now handled by LanguageControlsGrid
  const [loadingVocabulary, setLoadingVocabulary] = useState<boolean>(false);
  const [loadingExistingImages, setLoadingExistingImages] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // --- Computed Values ---
  const sourceLanguageCode = LANG_CODE_MAP[sourceLanguage];
  
  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);

  // Filter existing images by base language
  useEffect(() => {
    if (baseLanguageFilter === "all") {
      setFilteredExistingImages(existingImages);
    } else {
      const filtered = existingImages.filter(image => image.baseLanguage === baseLanguageFilter);
      setFilteredExistingImages(filtered);
    }
  }, [existingImages, baseLanguageFilter]);

  // Get unique base languages from existing images for filter dropdown
  const availableBaseLanguages = useMemo(() => {
    const languages = Array.from(new Set(existingImages.map(img => img.baseLanguage)));
    return languages.sort();
  }, [existingImages]);

  // --- Effects ---
  // Reset state when sourceLanguage changes
  useEffect(() => {
    setSection("");
    setVocabularyWords([]);
    setVocabularyGroupData([]);
    setError("");
    setSelectedWordsBucket(new Set());
  }, [sourceLanguage]);

  useEffect(() => {
    const loadVocabularyWords = async () => {
      if (!section) {
        setVocabularyWords([]);
        setVocabularyGroupData([]);
        setSelectedWordsBucket(new Set());
        setExistingImages([]);
        setBaseLanguageFilter("all");
        return;
      }
      
      setLoadingVocabulary(true);
      setError("");
      try {
        // Get language codes for API call
        const targetLangCode = langCodeMap[sourceLanguage] || "ES";
        const baseLangCode = "EN"; // Assume English as base language for data entry
        
        console.log(`Loading ImageList vocabulary for section: ${section}, target: ${targetLangCode}, base: ${baseLangCode}`);
        const vocabData = await fetchVocabularyWords(section, targetLangCode, baseLangCode);
        
        // Extract unique target words only (like WordDescriptions does)
        const uniqueWords = Array.from(
          new Set(vocabData.map((w) => w.Targ_Word?.trim()).filter(Boolean))
        );
        setVocabularyWords(uniqueWords.sort());
        
        // Group vocabulary data by target word for background selection
        const groupedData: VocabularyGroupData[] = uniqueWords.map(targetWord => ({
          targetWord,
          allVariants: vocabData.filter(v => v.Targ_Word?.trim() === targetWord)
        }));
        setVocabularyGroupData(groupedData);
        
        setSelectedWordsBucket(new Set());
      } catch (err) {
        console.error("Error loading vocabulary:", err);
        setError("Failed to load vocabulary words");
      } finally {
        setLoadingVocabulary(false);
      }
    };

    const loadExistingImages = async () => {
      if (!section) {
        setExistingImages([]);
        setBaseLanguageFilter("all");
        return;
      }
      
      setLoadingExistingImages(true);
      try {
        const data = await apiClient.get(
          `${mobileAppUri}/api/v1/image-list`,
          { params: { section } }
        );
        if (data) {
          console.log("API Response for existing images:", data);
          
          // The API returns data wrapped in an 'items' array
          const items = data.items || [];
          
          // Transform the API data to match our ImageListData interface
          const transformedImages: ImageListData[] = items.map((item: any) => ({
            id: item.imageListId || item.id,
            selectedWords: Array.isArray(item.selectedWords) ? item.selectedWords : JSON.parse(item.selectedWords || '[]'),
            imageUrl: item.imageUrl,
            imageDescription: item.imageDescription,
            isGenerating: false,
            error: undefined,
            sectionId: item.sectionId,
            targetLanguage: item.targetLanguage,
            baseLanguage: item.baseLanguage,
            domain: item.domain,
            proficiencyLevel: item.proficiencyLevel,
            difficulty: item.difficulty,
            wordMappings: item.wordMappings ? (typeof item.wordMappings === 'string' ? JSON.parse(item.wordMappings) : item.wordMappings) : {},
            multiLanguageWordList: item.wordList ? (typeof item.wordList === 'string' ? JSON.parse(item.wordList) : item.wordList) : {}
          }));
          
          setExistingImages(transformedImages);
        } else {
          console.log("No existing images found");
          setExistingImages([]);
        }
      } catch (err) {
        console.error("Error loading existing images:", err);
        setExistingImages([]);
      } finally {
        setLoadingExistingImages(false);
      }
    };

    loadVocabularyWords();
    loadExistingImages();
  }, [section]);

  // --- Event Handlers ---
  const handleSourceLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSourceLanguage(e.target.value);
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSection(e.target.value);
    setError("");
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDomain(e.target.value);
  };

  const handleProficiencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setProficiencyLevel(e.target.value);
  };

  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDifficulty(e.target.value);
  };

  // --- Image Generation Logic ---
  const generateImageList = useCallback(async () => {
    if (selectedWordsBucket.size === 0) {
      setError("Please select at least one word");
      return;
    }

    const selectedWordsArray = Array.from(selectedWordsBucket);
    
    const newImageListItem: ImageListData = {
      id: generateUUID(),
      selectedWords: selectedWordsArray,
      isGenerating: true,
      sectionId: section,
      targetLanguage: sourceLanguageCode,
      baseLanguage: "EN",
      domain,
      proficiencyLevel,
      difficulty,
      wordMappings: {},
      multiLanguageWordList: {},
    };

    // Create comprehensive multi-language word mappings with both target and base words
    const multiLanguageWordList: { [languageCode: string]: Array<{targetWord: string, baseWord: string}> } = {};
    
    selectedWordsArray.forEach(word => {
      const groupData = vocabularyGroupData.find(g => g.targetWord === word);
      if (groupData && groupData.allVariants.length > 0) {
        // Use the first variant's base translation as the primary mapping
        newImageListItem.wordMappings[word] = groupData.allVariants[0].Base_Word;
        
        // Group all variants by their base language codes with target/base word pairs
        groupData.allVariants.forEach(variant => {
          const baseLangCode = variant.Base_Lang_Code?.toUpperCase();
          if (baseLangCode && variant.Base_Word && variant.Targ_Word) {
            if (!multiLanguageWordList[baseLangCode]) {
              multiLanguageWordList[baseLangCode] = [];
            }
            
            // Add unique target/base word pairs
            const existingPair = multiLanguageWordList[baseLangCode].find(
              pair => pair.targetWord === variant.Targ_Word && pair.baseWord === variant.Base_Word
            );
            if (!existingPair) {
              multiLanguageWordList[baseLangCode].push({
                targetWord: variant.Targ_Word,
                baseWord: variant.Base_Word
              });
            }
          }
        });
      }
    });
    
    // Also include the target language words (with themselves as base words for consistency)
    const targetLangCode = sourceLanguageCode?.toUpperCase();
    if (targetLangCode && !multiLanguageWordList[targetLangCode]) {
      multiLanguageWordList[targetLangCode] = selectedWordsArray.map(word => ({
        targetWord: word,
        baseWord: word // Target language words map to themselves
      }));
    }
    
    newImageListItem.multiLanguageWordList = multiLanguageWordList;

    setImageListData(prev => [...prev, newImageListItem]);
    setError("");

    try {
      // Generate AI prompt for image description
      // Only use target language words for the prompt
      const targetWordsList = selectedWordsArray.join(", ");
      
      console.log("=== TARGET WORDS ONLY ===");
      console.log("Target Language:", sourceLanguage);
      console.log("Target Words:", targetWordsList);
      console.log("========================");
      
      // Enhanced prompt for flat design educational cartoon style with cultural context - NO TEXT
      const culturalContext = {
          'Spanish': 'Set in a Spanish or Latin American context with people who look Spanish or Latino. Include Spanish/Latin American cultural elements like colorful colonial architecture, traditional clothing, cultural foods, and objects typical of Spanish-speaking countries',
          'German': 'Set in a German context with people who look German. Include German cultural elements like traditional German architecture, cultural clothing, German foods, and objects typical of Germany', 
          'French': 'Set in a French context with people who look French. Include French cultural elements like French architecture, traditional French clothing, French foods, and cultural objects typical of France',
          'Italian': 'Set in an Italian context with people who look Italian. Include Italian cultural elements like Italian architecture, traditional Italian clothing, Italian foods, and cultural objects typical of Italy',
          'Portuguese': 'Set in a Portuguese or Brazilian context with people who look Portuguese or Brazilian. Include Portuguese/Brazilian cultural elements like colorful Portuguese architecture, traditional clothing, cultural foods, and objects typical of Portuguese-speaking countries',
          'English': 'Set in an English-speaking context with people from English-speaking countries. Include cultural elements typical of English-speaking regions'
        }[sourceLanguage] || 'Include culturally appropriate people and background elements';
        
        // Create a natural scene description based on vocabulary themes instead of listing words
        const sceneDescription = createNaturalSceneFromWords(selectedWordsArray);
        
        // SKIP AI PROCESSING - Send directly to DALL-E to avoid unwanted interpretations
        const imagePrompt = `Create a flat design, 2D cartoon-style illustration showing ${sceneDescription}. Make sure to include these specific elements in the scene: ${targetWordsList}. Use minimal shading, bold black outlines, solid bright colors in a clean vector art style like children's book illustrations. ${culturalContext}. Style: flat design principles, no gradients, no complex shading. IMPORTANT: Do not include any visible text, labels, words, letters, or written language anywhere in the image.`;
        
        console.log("=== DIRECT DALL-E PROMPT (NO AI PROCESSING) ===");
        console.log("Direct prompt being sent to DALL-E:", imagePrompt);
        console.log("Scene Description:", sceneDescription);
        console.log("Target Words List:", targetWordsList);
        console.log("Cultural Context:", culturalContext);
        console.log("Source Language:", sourceLanguage);
        console.log("Selected Words:", selectedWordsArray);
        console.log("==============================================");

      // Generate image using the prompt
      console.log("=== FINAL DALL-E PROMPT ===");
      console.log("Final prompt being sent to DALL-E:", imagePrompt);
      console.log("===========================");
      
      const imagePayload = buildGenAiInitialImagePromptPayload(imagePrompt!);
      const imageUrl = await generateImage(imagePayload);

      if (!imageUrl) {
        throw new Error("Failed to generate image");
      }

      // Update the item with success
      setImageListData(prev => 
        prev.map(item => 
          item.id === newImageListItem.id 
            ? {
                ...item,
                imageUrl,
                imageDescription: imagePrompt,
                isGenerating: false,
              }
            : item
        )
      );

    } catch (err: any) {
      console.error("Error generating image list:", err);
      setImageListData(prev => 
        prev.map(item => 
          item.id === newImageListItem.id 
            ? {
                ...item,
                isGenerating: false,
                error: err.message || "Failed to generate image",
              }
            : item
        )
      );
    }
  }, [
    selectedWordsBucket,
    section,
    sourceLanguageCode,
    domain,
    proficiencyLevel,
    difficulty,
    vocabularyGroupData,
    sourceLanguage,
  ]);


  // Handler for word selection (matches WordDescriptions pattern)
  const toggleWord = (word: string) => {
    setSelectedWordsBucket((prev) => {
      const next = new Set(prev);
      next.has(word) ? next.delete(word) : next.add(word);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelectedWordsBucket(new Set(vocabularyWords));
  };

  const handleClearSelection = () => {
    setSelectedWordsBucket(new Set());
  };

  const handleDeleteImageList = (id: string) => {
    setImageListData(prev => prev.filter(item => item.id !== id));
  };

  const handleDeleteExistingImage = async (id: string) => {
    try {
      await apiClient.request({
        method: 'delete',
        url: `${dataLoaderUri}/api/v1/image-list`,
        data: { imageListId: id }
      });

      setExistingImages(prev => prev.filter(item => item.id !== id));
      setError("✅ Image deleted successfully");
    } catch (err: any) {
      console.error("Error deleting image:", err);
      setError(`Failed to delete image: ${err.message}`);
    }
  };

  const handleSaveImageList = async (id: string) => {
    const item = imageListData.find(item => item.id === id);
    if (!item || !item.imageUrl) {
      setError("Cannot save: No image generated");
      return;
    }

    try {
      setError("");
      console.log("=== SAVING IMAGE LIST ===");
      console.log("Item to save:", item);
      console.log("Multi Language Word List:", item.multiLanguageWordList);

      // Create a save entry for each base language, but skip if base language equals target language
      const savePromises = Object.entries(item.multiLanguageWordList).map(async ([languageCode, wordPairs]) => {
        if (wordPairs.length === 0) return null;
        
        // Skip saving when base language equals target language
        if (languageCode === sourceLanguageCode) {
          console.log(`Skipping save for ${languageCode} because it matches target language ${sourceLanguageCode}`);
          return null;
        }

        // Get the vocabulary identifiers for the words in this base language
        const selectedWordIds = item.selectedWords.map(targetWord => {
          const groupData = vocabularyGroupData.find(g => g.targetWord === targetWord);
          if (groupData) {
            // Find the variant that matches this base language
            const variant = groupData.allVariants.find(v => v.Base_Lang_Code?.toUpperCase() === languageCode);
            return variant?.Identifier || null;
          }
          return null;
        }).filter(Boolean); // Remove null values

        if (selectedWordIds.length === 0) {
          console.log(`No word identifiers found for ${languageCode}, skipping...`);
          return null;
        }

        // Only include data for this specific base language (not all languages)
        const baseLanguageWordList = { [languageCode]: wordPairs };

        const saveData = {
          sectionId: item.sectionId,
          imageListId: `${item.id}_${languageCode}`, // Unique ID per language
          selectedWords: selectedWordIds, // Use identifiers instead of words
          imageUrl: item.imageUrl,
          imageDescription: item.imageDescription,
          baseLanguage: languageCode, // The specific base language we're saving for
          targetLanguage: sourceLanguageCode, // The target language (what user selected)
          domain: item.domain,
          proficiencyLevel: item.proficiencyLevel,
          difficulty: item.difficulty,
          wordList: baseLanguageWordList // Only this base language, not all
        };

        console.log(`Saving for language ${languageCode}:`, saveData);

        // Call the POST API we built using dataLoaderUri
        return await apiClient.post(
          `${dataLoaderUri}/api/v1/image-list`,
          saveData
        );
      });

      // Wait for all saves to complete
      const results = await Promise.all(savePromises);
      const successfulSaves = results.filter(result => result !== null);

      console.log(`Successfully saved ${successfulSaves.length} language entries`);
      setError(`✅ Successfully saved image list for ${successfulSaves.length} languages`);

      // Optionally remove from local state since it's now saved
      // setImageListData(prev => prev.filter(item => item.id !== id));

    } catch (err: any) {
      console.error("Error saving image list:", err);
      setError(`Failed to save image list: ${err.message}`);
    }
  };

  const handleRegenerateImage = async (id: string) => {
    const item = imageListData.find(item => item.id === id);
    if (!item) return;

    setImageListData(prev => 
      prev.map(item => 
        item.id === id 
          ? { ...item, isGenerating: true, error: undefined }
          : item
      )
    );

    try {
      // Use existing description or generate new one
      let imagePrompt = item.imageDescription;
      
      if (!imagePrompt) {
        // Only use target language words for the prompt (same as initial generation)
        const targetWordsList = item.selectedWords.join(", ");
        
        console.log("=== TARGET WORDS ONLY (REGENERATE) ===");
        console.log("Target Language:", sourceLanguage);
        console.log("Target Words:", targetWordsList);
        console.log("=====================================");
        
        // Enhanced prompt for flat design educational cartoon style with cultural context - NO TEXT
        const culturalContext = {
          'Spanish': 'Set in a Spanish or Latin American context with people who look Spanish or Latino. Include Spanish/Latin American cultural elements like colorful colonial architecture, traditional clothing, cultural foods, and objects typical of Spanish-speaking countries',
          'German': 'Set in a German context with people who look German. Include German cultural elements like traditional German architecture, cultural clothing, German foods, and objects typical of Germany', 
          'French': 'Set in a French context with people who look French. Include French cultural elements like French architecture, traditional French clothing, French foods, and cultural objects typical of France',
          'Italian': 'Set in an Italian context with people who look Italian. Include Italian cultural elements like Italian architecture, traditional Italian clothing, Italian foods, and cultural objects typical of Italy',
          'Portuguese': 'Set in a Portuguese or Brazilian context with people who look Portuguese or Brazilian. Include Portuguese/Brazilian cultural elements like colorful Portuguese architecture, traditional clothing, cultural foods, and objects typical of Portuguese-speaking countries',
          'English': 'Set in an English-speaking context with people from English-speaking countries. Include cultural elements typical of English-speaking regions'
        }[sourceLanguage] || 'Include culturally appropriate people and background elements';
        
        // Create a natural scene description based on vocabulary themes instead of listing words
        const sceneDescription = createNaturalSceneFromWords(item.selectedWords);
        
        // SKIP AI PROCESSING - Send directly to DALL-E to avoid unwanted interpretations
        imagePrompt = `Create a flat design, 2D cartoon-style illustration showing ${sceneDescription}. Make sure to include these specific elements in the scene: ${targetWordsList}. Use minimal shading, bold black outlines, solid bright colors in a clean vector art style like children's book illustrations. ${culturalContext}. Style: flat design principles, no gradients, no complex shading. IMPORTANT: Do not include any visible text, labels, words, letters, or written language anywhere in the image.`;
        
        console.log("=== DIRECT DALL-E PROMPT REGENERATE (NO AI PROCESSING) ===");
        console.log("Direct prompt being sent to DALL-E:", imagePrompt);
        console.log("Scene Description:", sceneDescription);
        console.log("Target Words List:", targetWordsList);
        console.log("Cultural Context:", culturalContext);
        console.log("Source Language:", sourceLanguage);
        console.log("Selected Words:", item.selectedWords);
        console.log("=========================================================");
      }

      console.log("=== FINAL DALL-E PROMPT (REGENERATE) ===");
      console.log("Final prompt being sent to DALL-E:", imagePrompt);
      console.log("=======================================");

      const imagePayload = buildGenAiInitialImagePromptPayload(imagePrompt!);
      const imageUrl = await generateImage(imagePayload);

      if (!imageUrl) {
        throw new Error("Failed to regenerate image");
      }

      setImageListData(prev => 
        prev.map(item => 
          item.id === id 
            ? {
                ...item,
                imageUrl,
                imageDescription: imagePrompt,
                isGenerating: false,
              }
            : item
        )
      );

    } catch (err: any) {
      console.error("Error regenerating image:", err);
      setImageListData(prev => 
        prev.map(item => 
          item.id === id 
            ? {
                ...item,
                isGenerating: false,
                error: err.message || "Failed to regenerate image",
              }
            : item
        )
      );
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Image List Generator</h1>
      <p className={styles.subtitle}>
        Generate images with vocabulary words for users to identify and type
      </p>

      {/* Language Controls Grid */}
      <LanguageControlsGrid
        sourceLanguage={sourceLanguage}
        section={section}
        domain={domain}
        proficiencyLevel={proficiencyLevel}
        difficulty={difficulty}
        isGenerating={isLoading}
        loadingVocabulary={loadingVocabulary}
        onSourceLanguageChange={handleSourceLanguageChange}
        onSectionChange={handleSectionChange}
        onDomainChange={handleDomainChange}
        onProficiencyChange={handleProficiencyChange}
        onDifficultyChange={handleDifficultyChange}
        error={error}
      />

      {sourceLanguage && section && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>
            Select Vocabulary from "{section}" ({sourceLanguage})
          </h2>
          {loadingVocabulary && (
            <p className={styles.loadingMessage}>Loading vocabulary...</p>
          )}
          {!loadingVocabulary && vocabularyWords.length === 0 && !error && (
            <p className={styles.infoMessage}>
              No vocabulary words found for this section.
            </p>
          )}
          {!loadingVocabulary && vocabularyWords.length > 0 && (
            <>
              <div className={styles.tableContainer}>
                <table className={styles.vocabTable}>
                  <thead>
                    <tr>
                      <th style={{width: '60px'}}>Select</th>
                      <th>Vocabulary Word</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vocabularyWords.map((word, index) => (
                      <tr key={index}>
                        <td style={{textAlign: 'center'}}>
                          <input
                            type="checkbox"
                            checked={selectedWordsBucket.has(word)}
                            onChange={() => toggleWord(word)}
                            disabled={isLoading}
                          />
                        </td>
                        <td>{word}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {selectedWordsBucket.size > 0 && (
                <div className={styles.buttonContainer}>
                  <button
                    onClick={handleSelectAll}
                    className={styles.button}
                    disabled={isLoading}
                  >
                    Select All
                  </button>
                  <button
                    onClick={handleClearSelection}
                    className={styles.button}
                    disabled={isLoading}
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}


      {/* Generate Button */}
      <div className={styles.generateSection}>
        <button
          onClick={generateImageList}
          disabled={selectedWordsBucket.size === 0 || isLoading}
          className={styles.generateButton}
        >
          {isLoading
            ? "Generating Image..."
            : `Generate Image List (${selectedWordsBucket.size} words)`}
        </button>
      </div>

      {/* Error Display */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Existing Images Section */}
      {sourceLanguage && section && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>
            Existing Images for "{section}" ({sourceLanguage})
          </h2>
          
          {loadingExistingImages && (
            <p className={styles.loadingMessage}>Loading existing images...</p>
          )}
          
          {!loadingExistingImages && existingImages.length > 0 && (
            <div className={styles.filterSection}>
              <label className={styles.filterLabel}>Filter by Base Language:</label>
              <select
                value={baseLanguageFilter}
                onChange={(e) => setBaseLanguageFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Languages ({existingImages.length})</option>
                {availableBaseLanguages.map(lang => {
                  const count = existingImages.filter(img => img.baseLanguage === lang).length;
                  return (
                    <option key={lang} value={lang}>
                      {lang} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
          )}
          
          {!loadingExistingImages && existingImages.length === 0 && (
            <p className={styles.infoMessage}>
              No existing images found for this section.
            </p>
          )}
          
          {!loadingExistingImages && existingImages.length > 0 && filteredExistingImages.length === 0 && (
            <p className={styles.infoMessage}>
              No images found for the selected base language "{baseLanguageFilter}".
            </p>
          )}
          
          {!loadingExistingImages && filteredExistingImages.length > 0 && (
            <div className={styles.existingImagesGrid}>
              {filteredExistingImages.map((item) => (
                <div key={item.id} className={styles.existingImageCard}>
                  <div className={styles.existingImageHeader}>
                    <h4>
                      {item.baseLanguage} → {item.targetLanguage}
                    </h4>
                    <button
                      onClick={() => handleDeleteExistingImage(item.id)}
                      className={styles.deleteButton}
                      title="Delete this image"
                    >
                      Delete
                    </button>
                  </div>
                  
                  <div className={styles.existingImageContent}>
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt="Existing generated image"
                        className={styles.existingImage}
                      />
                    ) : (
                      <div className={styles.noImage}>No image available</div>
                    )}
                    
                    <div className={styles.existingImageInfo}>
                      <div className={styles.existingImageMeta}>
                        <strong>Level:</strong> {item.proficiencyLevel} |{" "}
                        <strong>Difficulty:</strong> {item.difficulty} |{" "}
                        <strong>Domain:</strong> {item.domain}
                      </div>
                      
                      {item.selectedWords && item.selectedWords.length > 0 && (
                        <div className={styles.existingWordsList}>
                          <strong>Word IDs:</strong> {item.selectedWords.join(", ")}
                        </div>
                      )}
                      
                      {item.imageDescription && (
                        <div className={styles.existingDescription}>
                          <strong>Description:</strong> {item.imageDescription}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Generated Images Section */}
      {imageListData.length > 0 && (
        <div className={styles.resultsSection}>
          <h3>Generated Image Lists</h3>
          
          {imageListData.map((item) => (
            <div key={item.id} className={styles.imageListCard}>
              <div className={styles.imageListHeader}>
                <h4>Image List - {item.selectedWords.length} words</h4>
                <div className={styles.imageListActions}>
                  {!item.isGenerating && (
                    <>
                      <button
                        onClick={() => handleRegenerateImage(item.id)}
                        className={styles.button}
                      >
                        Regenerate
                      </button>
                      <button
                        onClick={() => handleSaveImageList(item.id)}
                        className={styles.saveButton}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => handleDeleteImageList(item.id)}
                        className={styles.deleteButton}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className={styles.imageListContent}>
                <div className={styles.imageSection}>
                  {item.isGenerating ? (
                    <div className={styles.loading}>Generating image...</div>
                  ) : item.error ? (
                    <div className={styles.error}>{item.error}</div>
                  ) : item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt="Generated image"
                      className={styles.generatedImage}
                    />
                  ) : (
                    <div className={styles.noImage}>No image generated</div>
                  )}
                </div>

                <div className={styles.wordsSection}>
                  <h5>Selected Words:</h5>
                  <div className={styles.selectedWordsList}>
                    {item.selectedWords.map((word, index) => (
                      <span key={index} className={styles.selectedWord}>
                        {word}
                        {item.wordMappings[word] && (
                          <span className={styles.wordTranslation}>
                            ({item.wordMappings[word]})
                          </span>
                        )}
                      </span>
                    ))}
                  </div>

                  {item.imageDescription && (
                    <div className={styles.descriptionSection}>
                      <h5>Image Description:</h5>
                      <p className={styles.description}>{item.imageDescription}</p>
                    </div>
                  )}


                  <div className={styles.metadataSection}>
                    <div className={styles.metadata}>
                      <strong>Section:</strong> {item.sectionId} |{" "}
                      <strong>Level:</strong> {item.proficiencyLevel} |{" "}
                      <strong>Difficulty:</strong> {item.difficulty} |{" "}
                      <strong>Domain:</strong> {item.domain}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {loadingVocabulary && (
        <div className={styles.loadingMessage}>
          Loading vocabulary words...
        </div>
      )}
    </div>
  );
};

export default ImageList;